import type { RealtimeClientMessage, RealtimeNotice } from '@deskworks/domain';

export type RealtimeStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

type Handler = (notice: RealtimeNotice) => void;

export interface RealtimeOptions {
  /** Absolute ws(s):// URL of the realtime endpoint, e.g. wss://app.example/v1/realtime. */
  url: string;
  /** Obtains a short-lived ticket through an authenticated API call. */
  getTicket: () => Promise<string>;
  WebSocketImpl?: typeof WebSocket;
  onRevoked?: () => void;
}

/**
 * Realtime notices with automatic reconnect. Notices are only hints: after a reconnect,
 * subscribers receive a synthetic `reconnected` event so they can backfill from the API.
 */
export class RealtimeClient {
  private ws: WebSocket | null = null;
  private handlers = new Map<string, Set<Handler>>();
  private statusHandlers = new Set<(s: RealtimeStatus) => void>();
  private reconnectHandlers = new Set<() => void>();
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private ping: ReturnType<typeof setInterval> | null = null;
  private stopped = true;
  private everOpened = false;
  status: RealtimeStatus = 'idle';

  constructor(private readonly opts: RealtimeOptions) {}

  start() {
    if (!this.stopped) return;
    this.stopped = false;
    void this.connect();
  }

  stop() {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    if (this.ping) clearInterval(this.ping);
    this.ws?.close(1000, 'client stop');
    this.ws = null;
    this.setStatus('closed');
  }

  on(type: RealtimeNotice['type'] | '*', handler: Handler): () => void {
    const set = this.handlers.get(type) ?? new Set();
    set.add(handler);
    this.handlers.set(type, set);
    return () => void set.delete(handler);
  }

  onStatus(handler: (s: RealtimeStatus) => void): () => void {
    this.statusHandlers.add(handler);
    return () => void this.statusHandlers.delete(handler);
  }

  /** Called after a connection is re-established; use it to backfill missed changes. */
  onReconnected(handler: () => void): () => void {
    this.reconnectHandlers.add(handler);
    return () => void this.reconnectHandlers.delete(handler);
  }

  send(msg: RealtimeClientMessage) {
    if (this.ws?.readyState === 1) this.ws.send(JSON.stringify(msg));
  }

  /** Reconnect immediately (e.g. when the device comes back online or the tab regains focus). */
  nudge() {
    if (this.stopped || this.status === 'open' || this.status === 'connecting') return;
    if (this.timer) clearTimeout(this.timer);
    this.attempt = 0;
    void this.connect();
  }

  private setStatus(s: RealtimeStatus) {
    this.status = s;
    for (const h of this.statusHandlers) h(s);
  }

  private async connect() {
    if (this.stopped) return;
    this.setStatus(this.everOpened ? 'reconnecting' : 'connecting');
    let ticket: string;
    try {
      ticket = await this.opts.getTicket();
    } catch {
      this.schedule();
      return;
    }
    if (this.stopped) return;
    const WS = this.opts.WebSocketImpl ?? WebSocket;
    const ws = new WS(`${this.opts.url}?ticket=${encodeURIComponent(ticket)}`);
    this.ws = ws;
    ws.onopen = () => {
      const wasReconnect = this.everOpened;
      this.everOpened = true;
      this.attempt = 0;
      this.setStatus('open');
      if (this.ping) clearInterval(this.ping);
      this.ping = setInterval(() => this.send({ type: 'ping' }), 25_000);
      if (wasReconnect) for (const h of this.reconnectHandlers) h();
    };
    ws.onmessage = (ev) => {
      let notice: RealtimeNotice;
      try {
        notice = JSON.parse(String(ev.data)) as RealtimeNotice;
      } catch {
        return;
      }
      for (const h of this.handlers.get(notice.type) ?? []) h(notice);
      for (const h of this.handlers.get('*') ?? []) h(notice);
    };
    ws.onclose = (ev) => {
      if (this.ping) clearInterval(this.ping);
      if (this.ws === ws) this.ws = null;
      if (ev.code === 4401 && ev.reason === 'session revoked') {
        this.stopped = true;
        this.setStatus('closed');
        this.opts.onRevoked?.();
        return;
      }
      this.schedule();
    };
    ws.onerror = () => {
      ws.close();
    };
  }

  private schedule() {
    if (this.stopped) return;
    this.setStatus('reconnecting');
    const base = Math.min(30_000, 1000 * 2 ** Math.min(this.attempt, 5));
    const delay = base / 2 + Math.random() * (base / 2);
    this.attempt++;
    this.timer = setTimeout(() => void this.connect(), delay);
  }
}
